import React, { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ActionButton } from '../common/petCloud/ActionButton';
import { Dropdown, PetsCounter } from './common';

import { PET_DEMOS_SPECIES } from '../../constants';
import {
  DATE_FORMATS,
  formatDateByLanguage,
  formatShortDateByLanguage,
} from '../../services/language.utils';

import styles from './CompletePetRemoval.module.css';

const SinglePetDisclaimer = ({ petName = '', petSpecie = '' }) => (
  <span className={styles.singlePetDisclaimer}>
    <Trans
      components={{
        bold: <strong className={styles.singlePetDisclaimerBold} />,
      }}
      i18nKey="petDemos:completePetRemoval.singlePetDisclaimer"
      values={{ petName, petSpecie }}
    />
  </span>
);

const CompletePetRemoval = ({
  coveragePeriodSelected = {},
  onCompletePetRemovalContinue = () => { },
  onGoBackClick = () => { },
  onSelectedPetsChange = () => { },
  petRemovals = [],
  petQuantityToRemove = {
    catCount: 0,
    dogCount: 0,
    total: 0,
  },
  removalDates = [],
  selectedPets = [],
}) => {
  const { t } = useTranslation('petDemos');

  const coveragePeriodDates = useMemo(() => {
    if (!coveragePeriodSelected) {
      return {
        endDate: '',
        startDate: '',
      };
    }

    return {
      endDate: formatShortDateByLanguage(
        coveragePeriodSelected.coverageEndDate,
      ),
      startDate: formatShortDateByLanguage(
        coveragePeriodSelected.coverageStartDate,
      ),
    };
  }, [coveragePeriodSelected]);

  // Get all removal dates with formatted dates
  const removalDatesData = useMemo(() => {
    if (!removalDates.length) {
      return [];
    }

    return removalDates.map((removalDateEntry) => {
      const formattedDate = formatDateByLanguage({
        date: removalDateEntry.date,
        dateFormat: DATE_FORMATS.longWithMonthName,
      });

      return {
        ...removalDateEntry,
        formattedDate,
      };
    });
  }, [removalDates]);

  // Filter pets available for each removal date
  const getPetsForRemovalDate = (removalDateEntry) => {
    // Already selected under a different removal date
    const selectedForOtherDates = new Set(
      selectedPets
        .filter((pet) => pet.removalDate !== removalDateEntry.date)
        .map((pet) => pet.Id),
    );

    return petRemovals.filter((pet) => {
      if (selectedForOtherDates.has(pet.Id)) {
        return false;
      }

      // Match pets by species and removal date
      const petSpecies = pet.Specie;
      const matchesSpecies = (petSpecies === PET_DEMOS_SPECIES.dog
        && removalDateEntry.dogs > 0)
        || (petSpecies === PET_DEMOS_SPECIES.cat
          && removalDateEntry.cats > 0);

      // Check if pet's removal date is in range of coverage period
      const petDate = pet.coverageStartDate?.split('T')[0];
      const removalDate = removalDateEntry.date?.split('T')[0];
      const matchesDate = petDate <= removalDate;

      return matchesSpecies && matchesDate;
    });
  };

  // get selected pets by specific removal date
  const getSelectedPetsByRemovalDate = (removalDate) => selectedPets
    .filter((pet) => pet.removalDate === removalDate);

  // Validate continue button
  // Check all removal dates have required pets selected
  const continueDisabled = useMemo(() => {
    if (removalDatesData.length === 0) {
      return true;
    }

    return removalDatesData.some((removalDateEntry) => {
      const selectedPetsByRemovalDate = getSelectedPetsByRemovalDate(
        removalDateEntry.date,
      );
      const requiredCats = removalDateEntry.cats || 0;
      const requiredDogs = removalDateEntry.dogs || 0;
      const requiredTotal = requiredCats + requiredDogs;

      if (requiredTotal === 0) {
        return false; // No pets required for this date
      }

      const selectedCats = selectedPetsByRemovalDate.filter(
        (pet) => pet.Specie === PET_DEMOS_SPECIES.cat,
      ).length;
      const selectedDogs = selectedPetsByRemovalDate.filter(
        (pet) => pet.Specie === PET_DEMOS_SPECIES.dog,
      ).length;

      return selectedCats !== requiredCats
        || selectedDogs !== requiredDogs;
    });
  }, [removalDatesData, selectedPets, petRemovals]);

  const handlePetSelectionChange = (updatedPets, removalDateUpdated) => {
    onSelectedPetsChange(updatedPets, removalDateUpdated);
  };

  const validateSinglePetScenario = useCallback(({
    dogsRequired = 0,
    catsRequired = 0,
    petsForDate = [],
  }) => {
    // 1. only 1 dog required
    // 2. only 1 pet available for selecting
    // 3. no cats required
    const dog = dogsRequired === 1 && petsForDate.length === 1
      && catsRequired === 0 ? petsForDate[0] : null;

    // 1. only 1 cat required
    // 2. only 1 pet available for selecting
    // 3. no dogs required
    const cat = catsRequired === 1 && petsForDate.length === 1
      && dogsRequired === 0 ? petsForDate[0] : null;

    return {
      cat,
      dog,
      onlyOneCat: Boolean(cat),
      onlyOneDog: Boolean(dog),
    };
  }, []);

  const renderDropdownHeader = (removalDateEntry, petsForDate) => {
    const selectedPetsByDate = getSelectedPetsByRemovalDate(
      removalDateEntry.date,
    );
    const counters = [];

    const { cat, dog, onlyOneCat, onlyOneDog } = validateSinglePetScenario({
      catsRequired: removalDateEntry.cats,
      dogsRequired: removalDateEntry.dogs,
      petsForDate,
    });

    if (removalDateEntry.cats > 0) {
      counters.push({
        specietype: PET_DEMOS_SPECIES.cat,
        totalPets: removalDateEntry.cats,
      });
    }

    if (removalDateEntry.dogs > 0) {
      counters.push({
        specietype: PET_DEMOS_SPECIES.dog,
        totalPets: removalDateEntry.dogs,
      });
    }

    return (
      <div className={styles.column}>
        <div className={styles.dateAndCounterContainer}>
          <span className={styles.removalDate}>
            {removalDateEntry.formattedDate}
          </span>

          <div className={styles.petsCounterContainer}>
            {counters.map(({ specietype, totalPets }) => (
              <PetsCounter
                key={specietype}
                selectedPets={selectedPetsByDate}
                specietype={specietype}
                totalPets={totalPets}
              />
            ))}
          </div>
        </div>

        {onlyOneDog && (
          <SinglePetDisclaimer
            petName={dog.Name}
            petSpecie={dog.Specie}
          />
        )}

        {onlyOneCat && (
          <SinglePetDisclaimer
            petName={cat.Name}
            petSpecie={cat.Specie}
          />
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>
        {t('completePetRemoval.title')}
      </h1>

      <h3 className={styles.coveragePeriod}>
        <Trans
          components={{
            boldBlue: <span className={styles.blue} />,
          }}
          i18nKey="completePetRemoval.coveragePeriod"
          t={t}
          values={{
            endDate: coveragePeriodDates.endDate,
            startDate: coveragePeriodDates.startDate,
          }}
        />
      </h3>

      <hr className={styles.headerDivider} />

      <h2 className={styles.petsRemoved}>
        <span className={styles.blue}>
          {petQuantityToRemove.dogCount > 0
            && t('completePetRemoval.petsRemovedTitle.dog', {
              count: petQuantityToRemove.dogCount,
            })}

          {petQuantityToRemove.dogCount > 0 && petQuantityToRemove.catCount > 0
            && t('completePetRemoval.petsRemovedTitle.and')}

          {petQuantityToRemove.catCount > 0
            && t('completePetRemoval.petsRemovedTitle.cat', {
              count: petQuantityToRemove.catCount,
            })}
        </span>

        {t('completePetRemoval.petsRemovedTitle.common')}
      </h2>

      <span className={styles.disclaimer}>
        {t('completePetRemoval.disclaimer')}
      </span>

      <h4 className={styles.removalDates}>
        {t('completePetRemoval.removalDates')}
      </h4>

      <hr className={styles.headerDividerMobile} />

      <div className={styles.comboBoxContainer}>
        {removalDatesData.map((removalDateEntry, index) => {
          const petsForDate = getPetsForRemovalDate(removalDateEntry);
          const selectedPetsByDate = getSelectedPetsByRemovalDate(
            removalDateEntry.date,
          );
          const { cat, dog } = validateSinglePetScenario({
            catsRequired: removalDateEntry.cats,
            dogsRequired: removalDateEntry.dogs,
            petsForDate,
          });

          return (
            <div
              key={removalDateEntry.date || index}
              className={styles.removalDateSection}
            >
              <Dropdown
                disabledBecauseOneOption={cat || dog}
                headerComponent={renderDropdownHeader(
                  removalDateEntry,
                  petsForDate,
                )}
                onSelectionChange={(updatedPets) => {
                  handlePetSelectionChange(updatedPets, removalDateEntry.date);
                }}
                pets={petsForDate}
                removalDate={removalDateEntry.date}
                selectedPets={selectedPetsByDate}
              />
            </div>
          );
        })}
      </div>

      <div className={styles.buttonsContainer}>
        <ActionButton
          isSecondaryButton
          noMargin
          onClick={onGoBackClick}
          title={t('completePetRemoval.goBack')}
        />

        <ActionButton
          disabled={continueDisabled}
          noMargin
          onClick={onCompletePetRemovalContinue}
          title={t('completePetRemoval.continue')}
        />
      </div>
    </div>
  );
};

export { CompletePetRemoval };
